import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Home, Building2, Users2, Settings } from 'lucide-react';
import { getUser } from '../lib/auth';
import { getMaintenanceUrgentCount } from '../lib/api';

// Use Lucide's Building2 - clean, modern, professional
const PropertiesIcon = Building2;

// Custom Checkouts icon - Horizontal luggage with checkmark (sized to match Lucide icons)
const CheckoutsIcon = ({ size = 24, strokeWidth = 1.5, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={strokeWidth}
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    {/* Luggage handle on top */}
    <path d="M9 6V4.5C9 3.67157 9.67157 3 10.5 3H13.5C14.3284 3 15 3.67157 15 4.5V6" />
    {/* Horizontal rectangular luggage body (fills the viewBox, vertically centered) */}
    <rect x="2.5" y="6" width="19" height="15" rx="2.5" />
    {/* Checkmark inside, centered */}
    <path d="M8 13.5L11 16.5L16 10.5" />
  </svg>
);

export default function MobileNav() {
  const location = useLocation();
  const user = getUser();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'company_manager';
  const [urgentCount, setUrgentCount] = useState(0);

  useEffect(() => {
    if (!(isAdmin || isManager)) return;
    let active = true;
    const load = () => getMaintenanceUrgentCount()
      .then((res) => { if (active) setUrgentCount(res.data.count || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [isAdmin, isManager]);

  const navItems = [
    { to: '/home', icon: Home, label: 'Home' },
    { to: '/properties', icon: PropertiesIcon, label: 'Properties', badge: urgentCount },
    { to: '/checkouts', icon: CheckoutsIcon, label: 'Checkouts' },
  ];

  if (isAdmin || isManager) {
    navItems.push({ to: '/team', icon: Users2, label: 'Team' });
  }

  navItems.push({ to: '/settings', icon: Settings, label: 'Settings' });

  return (
    <>
      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-center">
          <nav className="flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to || 
                (item.to === '/properties' && location.pathname === '/dashboard') ||
                (item.to === '/checkouts' && location.pathname === '/logs');
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {item.label}
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="nav-urgent-badge">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed left-4 right-4 z-50" style={{ bottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <div className="bg-white rounded-[28px] shadow-xl shadow-slate-900/8 border border-slate-100/80 max-w-sm mx-auto px-2 py-1.5 flex justify-around items-center">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to || 
              (item.to === '/properties' && location.pathname === '/dashboard') ||
              (item.to === '/checkouts' && location.pathname === '/logs');
            
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                data-tour={`nav-${item.label.toLowerCase()}`}
                className="flex flex-col items-center justify-center min-w-[52px]"
              >
                <div className={`relative p-1.5 rounded-xl transition-all ${
                  isActive ? 'bg-emerald-50' : ''
                }`}>
                  <Icon 
                    size={22} 
                    strokeWidth={isActive ? 2 : 1.5}
                    className={`transition-colors ${
                      isActive ? 'text-emerald-500' : 'text-slate-700'
                    }`}
                  />
                  {item.badge > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="nav-urgent-badge-mobile">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-semibold mt-0.5 uppercase tracking-wide ${
                  isActive ? 'text-emerald-600' : 'text-slate-700'
                }`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}

// Export for use in other components
export { PropertiesIcon, CheckoutsIcon };
